import { PANEL_URL } from '@/config/brand';

export { PANEL_URL };
export const PANEL_HOST = new URL(PANEL_URL).host;

const REDIRECT_AT_KEY = 'ch_panel_redirect_at';
const REDIRECT_COUNT_KEY = 'ch_panel_redirect_count';
const LOOP_WINDOW_MS = 30000;
const MAX_AUTO_REDIRECTS = 2;

/** Public browse + auth pages stay on SPA; only non-public paths auto-redirect to panel. */
export function shouldSkipPanelRedirect(pathname) {
  return (
    pathname === '/' ||
    pathname === '/listings' ||
    pathname === '/map' ||
    pathname.startsWith('/listing/') ||
    pathname === '/advertise' ||
    pathname.startsWith('/advertise/') ||
    pathname === '/about' ||
    pathname === '/contact' ||
    pathname === '/help' ||
    pathname === '/privacy' ||
    pathname === '/terms' ||
    pathname === '/cookies' ||
    pathname === '/delete-account' ||
    pathname === '/login' ||
    pathname === '/register' ||
    pathname === '/agents' ||
    pathname.startsWith('/agents/') ||
    pathname === '/dashboard' ||
    pathname.startsWith('/dashboard/') ||
    pathname.startsWith('/auth/verify') ||
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/forgot-password')
  );
}

/**
 * Relative in-app path only (open-redirect safe).
 * Allows pathname + optional search/hash; rejects //, schemes, backslashes, off-origin.
 */
export function isSafeReturnPath(candidate) {
  if (typeof candidate !== 'string') return false;
  let path = candidate.trim();
  if (!path) return false;
  try {
    path = decodeURIComponent(path);
  } catch {
    return false;
  }
  path = path.trim();
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  if (/[\0\r\n\\]/.test(path)) return false;
  if (/^\/\s*(https?:|javascript:|data:)/i.test(path)) return false;
  if (typeof window !== 'undefined' && window.location?.origin) {
    try {
      const resolved = new URL(path, window.location.origin);
      if (resolved.origin !== window.location.origin) return false;
      return true;
    } catch {
      return false;
    }
  }
  return true;
}

/** Safe in-app return path from login `?redirect=` and/or RequireAuth `state.from`. */
export function getPostLoginReturnPath(location) {
  const fromQuery = new URLSearchParams(location?.search || '').get('redirect');
  const fromState = location?.state?.from;
  const candidates = [fromQuery, fromState];
  for (const candidate of candidates) {
    if (isSafeReturnPath(candidate)) {
      try {
        return decodeURIComponent(candidate.trim());
      } catch {
        return candidate.trim();
      }
    }
  }
  return null;
}

export function clearPanelRedirectGuard() {
  try {
    sessionStorage.removeItem(REDIRECT_AT_KEY);
    sessionStorage.removeItem(REDIRECT_COUNT_KEY);
  } catch (_) {}
}

/** Navigate to fixed panel URL. Auto redirects are loop-guarded; pass { force: true } after real login. */
export function redirectToPanel({ force = false } = {}) {
  if (window.location.host === PANEL_HOST) return false;

  if (!force) {
    try {
      const last = Number(sessionStorage.getItem(REDIRECT_AT_KEY) || 0);
      const count = Number(sessionStorage.getItem(REDIRECT_COUNT_KEY) || 0);
      const withinWindow = Date.now() - last <= LOOP_WINDOW_MS;
      if (withinWindow && count >= MAX_AUTO_REDIRECTS) return false;
      sessionStorage.setItem(REDIRECT_AT_KEY, String(Date.now()));
      sessionStorage.setItem(REDIRECT_COUNT_KEY, String(withinWindow ? count + 1 : 1));
    } catch (_) {
      return false;
    }
  } else {
    clearPanelRedirectGuard();
  }

  window.location.href = PANEL_URL;
  return true;
}
